import { components } from 'react-select';
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { Link } from '@/shared/ui/link';
import {
  OptionWrapper,
  SearchOptionInputWrapper,
  NotFoundElement,
  ControlWrapper,
  OptionCheck,
  SearchOptionInput,
  ImageOption,
  OptionText,
  CategoryDrawerText,
  OptionCount,
  ImageControlWrapper,
  SelectWrapper,
  StyledSelect,
  OptionsWrapper,
} from './styles';
import { Image } from '@/shared/ui/image';
import { useProviders } from '../model/hooks';
import { withBreakpointsSlice } from '@/shared/providers/utils/with-breakpoints-slice';
import { Drawer } from '@/shared/ui/drawer';
import { Condition } from '@/shared/ui/condition';
import { EllipsisWrapper } from '@/features/select-category/ui/styles';
import {
  BodyTextBoldPrimary,
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
  BodyTextRegularTertiary,
} from '@/shared/ui/typography';
import { useI18n } from '@locales/client';
import { SkeletonLoader } from '@/shared/ui/skeleton';
import { BodyTextMediumB } from '@/shared/ui/typography2';

const DEFAULT_PROVIDER = 'all';
const IMAGE_SIZES = {
  MOBILE: 24,
  DESKTOP: 28,
};
const CHECK_ICON_SIZE = 16;
const ICON_PATHS = {
  ALL: '/images/icons/all-providers.svg',
  ALL_GRAY: '/images/icons/all-providers-gray.svg',
  CHECK: '/images/icons/check.svg',
  FILTERS: '/images/icons/all-filters.svg',
};

const getHref = (basePath: string, providerId: string) => {
  const pathSegment = providerId !== DEFAULT_PROVIDER ? `/${providerId}` : '';

  return `${basePath}${pathSegment}`;
};

const Control = ({ children, ...props }) => {
  const { providerParam, t, mobileLandscape } = props.selectProps.selectProps;
  return (
    <components.Control {...props}>
      <ControlWrapper>
        <ImageControlWrapper>
          <Image
            src={ICON_PATHS.FILTERS}
            width={mobileLandscape ? IMAGE_SIZES.MOBILE : IMAGE_SIZES.DESKTOP}
            height={mobileLandscape ? IMAGE_SIZES.MOBILE : IMAGE_SIZES.DESKTOP}
            alt="Provider-icon"
          />
        </ImageControlWrapper>
        {children}
      </ControlWrapper>
    </components.Control>
  );
};

const DropdownIndicator = () => null;

const Option = ({ children, ...props }) => {
  const { providerParam } = props.selectProps.selectProps;
  const pathname = usePathname();
  const basePath = pathname.substring(0, pathname.indexOf('games') + 5);
  const href = getHref(basePath, props.data.id);

  return (
    <components.Option {...props}>
      <Link href={href} legacyBehavior>
        <OptionWrapper $isSelected={props.isSelected} as="a">
          <ImageOption
            $isAll={props.data.id === DEFAULT_PROVIDER}
            $isActive={props.data.id === providerParam}
            src={
              props.data.id === DEFAULT_PROVIDER
                ? props.data.imageUrlGray
                : props.data.iconUrl
            }
            width={IMAGE_SIZES.MOBILE}
            height={IMAGE_SIZES.MOBILE}
            alt="indicator"
          />
          <OptionText>
            {children}
            <OptionCount>{props.data.gamesCount}</OptionCount>
          </OptionText>
          {!!props.isSelected && (
            <OptionCheck>
              <Image
                src={ICON_PATHS.CHECK}
                width={CHECK_ICON_SIZE}
                height={CHECK_ICON_SIZE}
                alt="Checkmark"
              />
            </OptionCheck>
          )}
        </OptionWrapper>
      </Link>
    </components.Option>
  );
};

const SingleValue = ({ children, ...props }) => {
  const { mobileLandscape, t, searchFocused } = props.selectProps.selectProps;
  return !searchFocused ? (
    <components.SingleValue {...props}>
      <BodyTextMediumB>{t('bonus.card.modal_providers')}</BodyTextMediumB>
    </components.SingleValue>
  ) : null;
};

const CustomValueContainer = ({ children, selectProps, ...props }) => {
  const t = selectProps.selectProps.t;
  return (
    <components.ValueContainer {...props} selectProps={selectProps}>
      {React.Children.map(children, (child) => {
        return (
          child ||
          (props.hasValue ? (
            <components.SingleValue
              {...props}
              isFocused={selectProps.isFocused}
              isDisabled={selectProps.isDisabled}
            >
              <BodyTextMediumB>
                {t('bonus.card.modal_providers')}
              </BodyTextMediumB>
            </components.SingleValue>
          ) : (
            <components.Placeholder
              {...props}
              key="placeholder"
              isDisabled={selectProps.isDisabled}
              data={props.getValue()}
            >
              {selectProps.placeholder}
            </components.Placeholder>
          ))
        );
      })}
    </components.ValueContainer>
  );
};

const CustomMenuList = ({ selectProps, ...props }) => {
  const { onInputChange, inputValue, onMenuInputFocus } = selectProps;
  const t = selectProps.selectProps.t;

  const ariaAttributes = {
    'aria-autocomplete': 'list',
    'aria-label': selectProps['aria-label'],
    'aria-labelledby': selectProps['aria-labelledby'],
  };

  return (
    <>
      <SearchOptionInputWrapper>
        <SearchOptionInput
          $isHasValue={inputValue !== ''}
          autoCorrect="off"
          autoComplete="off"
          spellCheck="false"
          type="text"
          value={inputValue}
          onChange={(e) =>
            onInputChange(e.currentTarget.value, {
              action: 'input-change',
            })
          }
          onMouseDown={(e) => {
            e.stopPropagation();
            e.target.focus();
          }}
          onTouchEnd={(e) => {
            e.stopPropagation();
            e.target.focus();
          }}
          onFocus={onMenuInputFocus}
          placeholder={t('common.filter.provider.search')}
          {...ariaAttributes}
        />
      </SearchOptionInputWrapper>
      <components.MenuList {...props} selectProps={selectProps} />
    </>
  );
};

const NoOptionsMessage = (props) => {
  return (
    <NotFoundElement>
      <components.NoOptionsMessage {...props} />
    </NotFoundElement>
  );
};

const SelectProvider = ({ mobileLandscape, searchFocused }) => {
  const { providers, isLoading } = useProviders();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const t = useI18n();

  const categoryFilter = searchParams.get('filter');
  const pathSegments = pathname.match(/\/games\/([^\/]+)/)?.[1];
  const providerParam = pathSegments ?? DEFAULT_PROVIDER;
  const [mobileDrawerIsOpen, setMobileDrawerIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const basePath = pathname.substring(0, pathname.indexOf('games') + 5);

  const allProviderOption = useMemo(
    () => ({
      id: DEFAULT_PROVIDER,
      imageUrl: ICON_PATHS.ALL,
      imageUrlGray: ICON_PATHS.ALL_GRAY,
      gamesCount:
        providers?.reduce((acc, provider) => acc + provider.gamesCount, 0) ?? 0,
    }),
    [providers]
  );

  const options = useMemo(
    () => [allProviderOption, ...(providers || [])],
    [providers, allProviderOption]
  );

  const [drawerOptions, setDrawerOptions] = useState(options);

  useEffect(() => {
    if (!isLoading) {
      setDrawerOptions(options);
    }
  }, [isLoading, options]);

  useEffect(() => {
    if (mobileLandscape) {
      const filteredOptions = options.filter((option) =>
        option.id?.toLowerCase().includes(inputValue.toLowerCase())
      );
      setDrawerOptions(inputValue ? filteredOptions : options);
    }
  }, [inputValue, mobileLandscape, options]);

  const onDomClick = useCallback((e) => {
    const menu = containerRef?.current?.querySelector('.react-select__menu');
    if (
      !containerRef.current?.contains(e.target) ||
      !menu?.contains(e.target)
    ) {
      setIsFocused(false);
      setInputValue('');
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', onDomClick);
    return () => document.removeEventListener('mousedown', onDomClick);
  }, [onDomClick]);

  const handleDrawerClose = useCallback(() => {
    setMobileDrawerIsOpen(false);
    setInputValue('');
  }, []);

  const renderProviderOption = useCallback(
    (option) => {
      const href = getHref(basePath, option.id);

      return (
        <Link href={href} legacyBehavior key={option.id}>
          <OptionWrapper
            as="a"
            onClick={() => setMobileDrawerIsOpen(false)}
            $haveDrawer={true}
            $isSelected={option.id === providerParam}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ImageOption
                $isAll={option.id === DEFAULT_PROVIDER}
                $isActive={option.id === providerParam}
                src={
                  option.id === DEFAULT_PROVIDER
                    ? option.imageUrlGray
                    : option.iconUrl
                }
                width={IMAGE_SIZES.MOBILE}
                height={IMAGE_SIZES.MOBILE}
                alt="indicator"
              />
              <OptionText>
                {option.id === DEFAULT_PROVIDER
                  ? t('common.filter.provider.all')
                  : option.title}
              </OptionText>
            </div>
            <OptionCount>{option.gamesCount}</OptionCount>
          </OptionWrapper>
        </Link>
      );
    },
    [basePath, categoryFilter, providerParam, t]
  );

  if (isLoading) {
    return (
      <SkeletonLoader
        containerClassName="flex-1"
        width={mobileLandscape ? '100%' : 240}
        height={40}
      />
    );
  }

  if (!mobileLandscape) {
    return (
      <SelectWrapper ref={containerRef}>
        <StyledSelect
          value={{ title: providerParam, id: providerParam }}
          unstyled
          selectProps={{
            options,
            providerParam,
            t,
            mobileLandscape,
            searchFocused,
          }}
          components={{
            Control,
            Option,
            MenuList: CustomMenuList,
            ValueContainer: CustomValueContainer,
            SingleValue,
            NoOptionsMessage,
            DropdownIndicator,
          }}
          onMenuInputFocus={() => setIsFocused(true)}
          onChange={() => setIsFocused(false)}
          onInputChange={setInputValue}
          noOptionsMessage={() => t('common.filter.provider.nothing')}
          isSearchable={false}
          classNamePrefix="react-select"
          getOptionLabel={(option) =>
            option.id === DEFAULT_PROVIDER
              ? t('common.filter.provider.all')
              : option.title
          }
          getOptionValue={(option) => option.id}
          options={options}
          closeMenuOnSelect
          menuIsOpen={isFocused || undefined}
          isFocused={isFocused || undefined}
        />
      </SelectWrapper>
    );
  }

  return (
    <>
      <SelectWrapper onClick={() => setMobileDrawerIsOpen(true)}>
        <StyledSelect
          $searchFocused={searchFocused}
          selectProps={{
            drawerOptions,
            providerParam,
            t,
            mobileLandscape,
            searchFocused,
          }}
          value={{ id: providerParam }}
          unstyled
          components={{
            Control,
            SingleValue,
            DropdownIndicator,
          }}
          isSearchable={false}
          classNamePrefix="react-select"
          isDisabled={true}
          getOptionLabel={(option) =>
            option.id === DEFAULT_PROVIDER
              ? t('common.filter.provider.all')
              : option.title
          }
          getOptionValue={(option) => option.id}
          options={options}
          delimiter={false}
        />
      </SelectWrapper>
      <Drawer onClose={handleDrawerClose} isOpen={mobileDrawerIsOpen}>
        <CategoryDrawerText $haveDrawer={true}>
          {t('common.filter.label.provider')}
        </CategoryDrawerText>
        <SearchOptionInputWrapper $haveDrawer={true}>
          <SearchOptionInput
            $isHasValue={inputValue !== ''}
            $haveDrawer={true}
            autoCorrect="off"
            autoComplete="off"
            spellCheck="false"
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.target.focus();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.target.focus();
            }}
          />
        </SearchOptionInputWrapper>
        {drawerOptions.length === 0 && (
          <NotFoundElement $haveDrawer={true}>
            {t('common.filter.provider.nothing')}
          </NotFoundElement>
        )}
        <OptionsWrapper>
          {drawerOptions.map(renderProviderOption)}
        </OptionsWrapper>
      </Drawer>
    </>
  );
};

const SelectProviderWithBreakpointsSlice = withBreakpointsSlice(
  SelectProvider,
  ({ mobileLandscape }) => ({ mobileLandscape })
);

export { SelectProviderWithBreakpointsSlice as SelectProvider };
